/**
 * Lust Witch Widget
 * 
 * Compact profile widget for the Lust Witch deck system.
 * Shows current card, draw button, and links to full page.
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Sparkles, Wand2, Target, RefreshCw, Flame,
    ChevronRight, Check, Loader2, Plus, Clock
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE } from '../utils/api';

// Card type config
const CARD_TYPES = {
    action: { label: 'Actions', icon: Target, color: 'wine', timeline: '5m–1hr' },
    habit: { label: 'Habits', icon: RefreshCw, color: 'purple', timeline: '2×/day–Weekly' },
    goal: { label: 'Goals', icon: Flame, color: 'amber', timeline: '2–8 weeks' }
};

function LustWitchWidget() {
    const { activePersona, getAuthHeaders } = useAuth();

    const [loading, setLoading] = useState(true);
    const [drawState, setDrawState] = useState(null);
    const [deckCounts, setDeckCounts] = useState({ action: 0, habit: 0, goal: 0 });
    const [cooldowns, setCooldowns] = useState(null);
    const [drawing, setDrawing] = useState(false);
    const [completing, setCompleting] = useState(false);

    const fetchData = async () => {
        if (!activePersona) {
            setLoading(false);
            return;
        }

        try {
            const headers = await getAuthHeaders();

            // Fetch deck counts
            const deckRes = await fetch(`${API_BASE}/api/lust-witch/deck/${activePersona.id}`, { headers });
            if (deckRes.ok) {
                const deckData = await deckRes.json();
                setDeckCounts(deckData.counts || { action: 0, habit: 0, goal: 0 });
            }

            // Fetch draw state
            const drawRes = await fetch(`${API_BASE}/api/lust-witch/draw/${activePersona.id}`, { headers });
            if (drawRes.ok) {
                const drawData = await drawRes.json();
                setDrawState(drawData);
            }

            // Fetch cooldowns
            const cdRes = await fetch(`${API_BASE}/api/lust-witch/cooldowns/${activePersona.id}`, { headers });
            if (cdRes.ok) {
                const cdData = await cdRes.json();
                setCooldowns(cdData);
            }
        } catch (error) {
            console.error('Error fetching Lust Witch data:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [activePersona]);

    const handleDraw = async () => {
        if (!activePersona || drawing) return;

        setDrawing(true);
        try {
            const headers = await getAuthHeaders();
            const res = await fetch(`${API_BASE}/api/lust-witch/draw/${activePersona.id}`, {
                method: 'POST',
                headers
            });

            if (res.ok) {
                const data = await res.json();
                setDrawState(prev => ({ ...prev, ...data, canDraw: false }));
            }
        } catch (error) {
            console.error('Error drawing card:', error);
        } finally {
            setDrawing(false);
        }
    };

    const handleComplete = async (action) => {
        if (!activePersona || completing) return;

        setCompleting(true);
        try {
            const headers = await getAuthHeaders();
            const res = await fetch(`${API_BASE}/api/lust-witch/complete/${activePersona.id}`, {
                method: 'POST',
                headers: { ...headers, 'Content-Type': 'application/json' },
                body: JSON.stringify({ action })
            });

            if (res.ok) {
                await fetchData(); // Refresh state
            }
        } catch (error) {
            console.error('Error completing card:', error);
        } finally {
            setCompleting(false);
        }
    };

    // Format time remaining
    const formatTimeRemaining = (isoDate) => {
        if (!isoDate) return null;
        const remaining = new Date(isoDate) - new Date();
        if (remaining <= 0) return null;

        const hours = Math.floor(remaining / (1000 * 60 * 60));
        const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));

        if (hours > 0) return `${hours}h ${minutes}m`;
        return `${minutes}m`;
    };

    if (!activePersona) return null;

    if (loading) {
        return (
            <div className="glass-card p-6 border border-purple-500/30">
                <div className="flex items-center gap-3 mb-4">
                    <Wand2 className="w-5 h-5 text-purple-400" />
                    <h3 className="font-display font-bold text-cream">Lust Witch</h3>
                </div>
                <div className="flex items-center justify-center py-6">
                    <Loader2 className="w-6 h-6 animate-spin text-purple-400" />
                </div>
            </div>
        );
    }

    const totalCards = deckCounts.action + deckCounts.habit + deckCounts.goal;
    const currentCard = drawState?.currentCard;
    const canDraw = drawState?.canDraw && deckCounts.action > 0;
    const nextDrawTime = formatTimeRemaining(drawState?.state?.next_draw_utc);
    const streak = drawState?.state?.streak_count || 0;

    return (
        <div className="glass-card border border-purple-500/30 overflow-hidden">
            {/* Header */}
            <div className="p-4 border-b border-purple-500/20 bg-gradient-to-r from-purple-900/20 to-transparent">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-purple-900/50 flex items-center justify-center">
                            <Wand2 className="w-5 h-5 text-purple-400" />
                        </div>
                        <div>
                            <h3 className="font-display font-bold text-cream">Lust Witch</h3>
                            <p className="text-xs text-purple-400">Arousal Guidance</p>
                        </div>
                    </div>
                    {streak > 0 && (
                        <div className="flex items-center gap-1 px-2 py-1 bg-amber-900/30 text-amber-400 text-xs">
                            <Flame size={12} />
                            <span>{streak} streak</span>
                        </div>
                    )}
                </div>
            </div>

            {/* Empty deck state */}
            {totalCards === 0 && (
                <div className="p-6 text-center">
                    <Sparkles className="w-10 h-10 mx-auto mb-3 text-purple-400/50" />
                    <p className="text-cream/70 mb-4">Build your first deck</p>
                    <Link
                        to="/lust-witch"
                        className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white text-sm transition-colors"
                    >
                        <Plus size={14} />
                        Add Cards
                    </Link>
                </div>
            )}

            {/* Current drawn card */}
            {currentCard && (
                <div className="p-4">
                    <motion.div
                        initial={{ rotateY: 180, opacity: 0 }}
                        animate={{ rotateY: 0, opacity: 1 }}
                        transition={{ duration: 0.5 }}
                        className="bg-gradient-to-br from-purple-900/40 to-wine-900/40 border border-purple-500/30 p-4"
                    >
                        <div className="text-xs text-purple-400 mb-1">Current Task</div>
                        <h4 className="font-bold text-cream mb-2">
                            {currentCard.arousal_mechanics?.name || 'Unknown'}
                        </h4>
                        <p className="text-sm text-cream/60 mb-4 line-clamp-2">
                            {currentCard.arousal_mechanics?.summary}
                        </p>

                        <div className="flex gap-2">
                            <button
                                onClick={() => handleComplete('complete')}
                                disabled={completing}
                                className="flex-1 flex items-center justify-center gap-2 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-sm transition-colors disabled:opacity-50"
                            >
                                {completing ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                                Complete
                            </button>
                            <button
                                onClick={() => handleComplete('reroll')}
                                disabled={completing}
                                className="px-4 py-2 bg-dark-700 hover:bg-dark-600 text-cream/70 text-sm transition-colors disabled:opacity-50"
                            >
                                <RefreshCw size={14} />
                            </button>
                        </div>
                    </motion.div>
                </div>
            )}

            {/* Draw button */}
            {totalCards > 0 && !currentCard && (
                <div className="p-4">
                    {canDraw ? (
                        <button
                            onClick={handleDraw}
                            disabled={drawing}
                            className="w-full py-4 bg-gradient-to-r from-purple-600 to-wine-600 hover:from-purple-500 hover:to-wine-500 text-white font-bold flex items-center justify-center gap-2 transition-all disabled:opacity-50"
                        >
                            {drawing ? (
                                <Loader2 className="w-5 h-5 animate-spin" />
                            ) : (
                                <>
                                    <Wand2 className="w-5 h-5" />
                                    Draw Action Card
                                </>
                            )}
                        </button>
                    ) : nextDrawTime ? (
                        <div className="text-center py-4 bg-dark-800/50">
                            <Clock className="w-6 h-6 mx-auto mb-2 text-purple-400/50" />
                            <p className="text-cream/50 text-sm">Next draw in</p>
                            <p className="text-purple-400 font-bold">{nextDrawTime}</p>
                        </div>
                    ) : deckCounts.action === 0 ? (
                        <div className="text-center py-4 bg-dark-800/50">
                            <p className="text-cream/50 text-sm mb-2">No action cards in deck</p>
                            <Link
                                to="/lust-witch"
                                className="text-purple-400 hover:text-purple-300 text-sm"
                            >
                                Add action cards →
                            </Link>
                        </div>
                    ) : null}
                </div>
            )}

            {/* Deck summary */}
            {totalCards > 0 && (
                <div className="px-4 pb-4">
                    <div className="grid grid-cols-3 gap-2">
                        {Object.entries(CARD_TYPES).map(([type, config]) => (
                            <div key={type} className="text-center p-2 bg-dark-800/50">
                                <div className="text-lg font-bold text-cream">
                                    {deckCounts[type] || 0}
                                </div>
                                <div className="text-xs text-dark-400">{config.label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Footer link */}
            <Link
                to="/lust-witch"
                className="flex items-center justify-between p-3 bg-dark-900/50 border-t border-purple-500/20 text-sm text-purple-400 hover:text-purple-300 hover:bg-dark-800/50 transition-colors"
            >
                <span>Manage Deck</span>
                <ChevronRight size={16} />
            </Link>
        </div>
    );
}

export default LustWitchWidget;
